/**
 * BillingSection: what this team is charged and how it pays (TRA-1163).
 *
 * Owner-only (the registry entry in sections.ts says so); TrainLoop staff
 * see it through read-only impersonation. It lived at /billing until
 * TRA-1185 moved it into Settings. Top to bottom (TRA-1380): the account
 * (plan, payment method, billing email), priced spend over the last 30
 * days from the gateway (the dashboard's figure, by day and by model),
 * this cycle (the accrual, with the lines the next invoice will carry
 * behind Show more), the team's own rates, and invoice history. The owner
 * opens the Stripe Customer Portal for the payment method and older
 * invoices.
 *
 * Three queries, so a Stripe hiccup blanks one section rather than the
 * page: /api/billing (overview), /api/billing/spend, /api/billing/invoices.
 * Spend and invoices mount on their own, not under the overview: when the
 * overview fails, the history a billing admin came for stays readable
 * (Devin round 3). Only a team the overview proves is never billed skips
 * them.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { ExternalLink, Loader2, Receipt } from 'lucide-react'
import { toast } from 'sonner'

import { customFetch, getApiErrorDetail } from '@/api/client'
import type {
  BillingInvoicesResponse,
  BillingOverviewResponse,
  BillingPortalRequest,
  BillingPortalResponse,
  BillingSpendResponse,
} from '@/api/generated/model'
import {
  MODE_DESCRIPTIONS,
  MODE_LABELS,
  cycleRows,
  formatUsd,
  spendTotals,
} from '@/components/billing/billing'
import { AccountFacts, CycleSummary } from '@/components/billing/BillingOverview'
import {
  CycleLinesTable,
  InvoicesTable,
  RatesTable,
  SpendByModelTable,
} from '@/components/billing/BillingTables'
import { PREVIEW_ROWS, ShowMore } from '@/components/billing/ShowMore'
import { SpendChart } from '@/components/billing/SpendChart'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { PUBLIC_API_BASE } from '@/lib/env'

import { SettingsPageHeader } from './SettingsPageHeader'
import { EYEBROW } from '@/components/ui/eyebrow'

import { SettingsBlock, SettingsSection } from './SettingsSection'

// The spend API behind this page (TRA-1193), for a dashboard of the
// customer's own. Same base as the evals snippet: local, test or prod,
// whichever this deployment serves. The snippet names no window, so the
// API applies its default, which is also the chart's.
const SPEND_CURL_SNIPPET = `curl ${PUBLIC_API_BASE}/v1/billing/spend \\
  -H "Authorization: Bearer $TRAINLOOP_API_KEY"`

export function BillingSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { activeTeamId, activeTeam, impersonatingTeamId } = useTeam()
  const canOpenPortal = impersonatingTeamId === null && (activeTeam?.is_owner ?? false)

  const overviewQuery = useQuery({
    queryKey: ['/api/billing', activeTeamId],
    queryFn: () => customFetch<BillingOverviewResponse>('/api/billing'),
    enabled: !!activeTeamId,
    retry: false,
  })
  const overview = overviewQuery.data
  const billed = overview !== undefined && overview.mode !== 'not_billed' ? overview : null
  // Never-billed is the one verdict that hides the history; an overview
  // that failed to load proves nothing, so the history still renders.
  const showHistory = billed !== null || overviewQuery.isError

  return (
    <div>
      <SettingsPageHeader
        section={section}
        badge={
          overview !== undefined ? (
            <Badge variant={overview.mode === 'usage' || overview.mode === 'gpu' ? 'success' : 'secondary'}>
              {MODE_LABELS[overview.mode]}
            </Badge>
          ) : undefined
        }
        action={billed !== null && canOpenPortal ? <PortalButton /> : undefined}
      />

      {overviewQuery.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : overviewQuery.isError ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(overviewQuery.error) ?? 'Billing could not be loaded.'}
          </p>
        </SettingsBlock>
      ) : overview !== undefined && billed === null ? (
        <SettingsBlock>
          <EmptyState
            icon={Receipt}
            title="This team is not billed."
            description={MODE_DESCRIPTIONS.not_billed}
          />
        </SettingsBlock>
      ) : null}

      {/* Spend and invoices load on their own, so the overview's three
          sections sit around them as siblings, not under one parent. */}
      {billed !== null && <AccountSection overview={billed} />}
      {showHistory && <SpendSection />}
      {billed !== null && <CycleSection overview={billed} />}
      {billed !== null && <RatesSection overview={billed} />}
      {showHistory && <InvoicesSection />}
    </div>
  )
}

function AccountSection({ overview }: { overview: BillingOverviewResponse }): React.JSX.Element {
  return (
    <SettingsSection
      title="Account"
      description="Change the payment method or billing contact in the Stripe portal."
    >
      <AccountFacts overview={overview} />
    </SettingsSection>
  )
}

/**
 * The accrual leads, and the lines behind it stay under Show more: a
 * reader who wants the figure has it without scrolling a table.
 */
function CycleSection({ overview }: { overview: BillingOverviewResponse }): React.JSX.Element {
  const { accrual } = overview
  return (
    <SettingsSection
      title="This cycle"
      description={
        accrual === null
          ? MODE_DESCRIPTIONS[overview.mode]
          : `${MODE_DESCRIPTIONS[overview.mode]} The cycle's lines are what Stripe will put on the next invoice. Models with no usage are left out.`
      }
    >
      {accrual === null ? (
        <p className="text-sm text-muted-foreground">No billing cycle is open.</p>
      ) : (
        <div className="space-y-6">
          <CycleSummary accrual={accrual} />
          <ShowMore rows={cycleRows(accrual.lines)} initial={0} noun="cycle lines">
            {(rows) => (
              <CycleLinesTable rows={rows} amountDue={accrual.amount_due} currency={accrual.currency} />
            )}
          </ShowMore>
        </div>
      )}
    </SettingsSection>
  )
}

function RatesSection({ overview }: { overview: BillingOverviewResponse }): React.JSX.Element {
  return (
    <SettingsSection
      title="Rates"
      description="Per-token rates are input, cached, and output per 1M tokens. Models with no rate of your own pay the catalog list price. An hourly model is billed as a dedicated deployment: a monthly base plus overage GPU-minutes."
    >
      <ShowMore rows={overview.rates} initial={PREVIEW_ROWS} noun="rates">
        {(rates) => <RatesTable rates={rates} />}
      </ShowMore>
    </SettingsSection>
  )
}

function SpendSection(): React.JSX.Element {
  const { activeTeamId } = useTeam()
  const spendQuery = useQuery({
    queryKey: ['/api/billing/spend', activeTeamId],
    queryFn: () => customFetch<BillingSpendResponse>('/api/billing/spend'),
    enabled: !!activeTeamId,
    retry: false,
  })
  return (
    <SettingsSection
      title="Spend, last 30 days"
      description="This chart shows your traffic priced by UTC day. On a dedicated GPU day, the figure is what the invoice charges. Models you pay a provider for directly are not priced here."
      aside={
        <p className="pt-2 text-xs text-muted-foreground">
          Pull these figures with a team API key.{' '}
          <a
            href={`${PUBLIC_API_BASE}/v1/docs`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-foreground underline underline-offset-4"
          >
            API reference
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </p>
      }
    >
      <div className="space-y-4">
        {spendQuery.isLoading ? (
          <Skeleton className="h-52 w-full" />
        ) : spendQuery.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(spendQuery.error) ?? 'Spend could not be loaded.'}
          </p>
        ) : spendQuery.data ? (
          <SpendFigures spend={spendQuery.data} />
        ) : null}
        <SpendSnippet />
      </div>
    </SettingsSection>
  )
}

function SpendFigures({ spend }: { spend: BillingSpendResponse }): React.JSX.Element {
  const { priced, unpricedDays } = spendTotals(spend.days)
  return (
    <>
      <div className="space-y-1">
        <p className={EYEBROW}>Priced</p>
        <p className="font-mono text-xl tabular-nums text-foreground">{formatUsd(priced)}</p>
      </div>
      <SpendChart days={spend.days} />
      {unpricedDays > 0 && (
        <p className="text-xs text-muted-foreground">
          Dashed rules mark days with traffic the gateway could not price, in full or in part. The
          total covers priced traffic only.
        </p>
      )}
      <div className="space-y-2">
        <p className={EYEBROW}>By model</p>
        <SpendByModelTable models={spend.models} />
      </div>
    </>
  )
}

function SpendSnippet(): React.JSX.Element {
  return (
    <div className="space-y-2">
      <pre className="max-w-full overflow-x-auto bg-muted p-3 text-left font-mono text-xs text-muted-foreground">
        {SPEND_CURL_SNIPPET}
      </pre>
      <p className="text-xs text-muted-foreground">
        Add start and end as UTC dates to change the window.
      </p>
    </div>
  )
}

function InvoicesSection(): React.JSX.Element {
  const { activeTeamId } = useTeam()
  const invoicesQuery = useQuery({
    queryKey: ['/api/billing/invoices', activeTeamId],
    queryFn: () => customFetch<BillingInvoicesResponse>('/api/billing/invoices'),
    enabled: !!activeTeamId,
    retry: false,
  })
  return (
    <SettingsSection title="Invoices" description="Stripe issues these invoices to this team.">
      {invoicesQuery.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : invoicesQuery.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {getApiErrorDetail(invoicesQuery.error) ?? 'Invoices could not be loaded.'}
        </p>
      ) : invoicesQuery.data ? (
        <InvoiceHistory history={invoicesQuery.data} />
      ) : null}
    </SettingsSection>
  )
}

function InvoiceHistory({ history }: { history: BillingInvoicesResponse }): React.JSX.Element {
  const { invoices } = history
  return (
    <ShowMore rows={invoices} initial={PREVIEW_ROWS} noun="invoices">
      {(visible) => (
        <div className="space-y-3">
          <InvoicesTable invoices={visible} />
          {/* The portal note belongs under the oldest invoice the page holds. */}
          {history.has_more && visible.length === invoices.length && (
            <p className="text-xs text-muted-foreground">Older invoices are in the Stripe portal.</p>
          )}
        </div>
      )}
    </ShowMore>
  )
}

function PortalButton(): React.JSX.Element {
  const portalMutation = useMutation({
    mutationFn: () =>
      customFetch<BillingPortalResponse>('/api/billing/portal', {
        method: 'POST',
        data: { return_url: window.location.href } satisfies BillingPortalRequest,
      }),
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorDetail(error) ?? 'The billing portal could not be opened.')
    },
  })
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={portalMutation.isPending}
      title="The Stripe portal has invoice history and your payment method."
      onClick={() => {
        portalMutation.mutate()
      }}
    >
      {portalMutation.isPending ? <Loader2 className="animate-spin" /> : <ExternalLink />}
      Manage billing
    </Button>
  )
}
